import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  CheckCircle,
  Clock,
  AlertCircle,
  Zap,
  Loader2,
  Plus,
  RefreshCw,
  GripVertical,
  Brain,
  Sparkles,
  Save,
  CheckCheck,
} from "lucide-react";
import API_CONFIG, { apiClient } from "../../../config/api";
import UnlinkIcon from "../../../Images/unlink.svg";



const STATUS_META = {
  completed: {
    className: "chip chip--success",
    color: "#10b981",
    icon: CheckCircle,
  },
  "in-progress": {
    className: "chip chip--warn",
    color: "#f59e0b",
    icon: Clock,
  },
  pending: {
    className: "chip chip--muted",
    color: "#6b7280",
    icon: AlertCircle,
  },
  default: {
    className: "chip",
    color: "#64748b",
    icon: AlertCircle,
  },
};

const INITIAL_FORM_STATE = {
  templateId: "",
  actionName: "",
  status: "pending",
  notes: "",
};

const INITIAL_RECOMMENDATION_FORM_STATE = {
  templateId: "",
  title: "",
  description: "",
  status: "open",
};

const normalizeArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.value)) return payload.value;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeGuid = (value) => {
  if (value === undefined || value === null) return null;
  return value.toString().trim().toLowerCase();
};

const getValueIgnoreCase = (object, targetKey) => {
  if (!object || typeof object !== "object") return undefined;
  const lowerTarget = targetKey.toLowerCase();
  const match = Object.keys(object).find(
    (key) => key.toLowerCase() === lowerTarget
  );
  if (match !== undefined) {
    return object[match];
  }
  return undefined;
};

const pluckBundleSection = (payload, keys = []) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  for (const key of keys) {
    const value = getValueIgnoreCase(payload, key);
    if (value !== undefined) {
      return value;
    }
  }

  if (payload.data && payload !== payload.data) {
    const nested = pluckBundleSection(payload.data, keys);
    if (nested) return nested;
  }

  if (payload.value && payload !== payload.value) {
    const nested = pluckBundleSection(payload.value, keys);
    if (nested) return nested;
  }

  return [];
};

const extractTemplateUUID = (payload) =>
  payload?.uuid ??
  payload?.UUID ??
  payload?.templateUUID ??
  payload?.TemplateUUID ??
  payload?.templateId ??
  payload?.TemplateId ??
  payload?.id ??
  payload?.ID ??
  payload?.responseActionTemplateId ??
  payload?.ResponseActionTemplateId ??
  payload?.data?.uuid ??
  payload?.data?.UUID ??
  payload?.data?.templateUUID ??
  payload?.data?.TemplateUUID ??
  payload?.data?.templateId ??
  payload?.data?.TemplateId ??
  payload?.data?.id ??
  payload?.data?.ID ??
  null;

const getTemplateLabel = (template) =>
  template?.actionName ||
  template?.ActionName ||
  template?.name ||
  template?.Name ||
  template?.title ||
  template?.Title ||
  template?.action ||
  template?.Action ||
  template?.actionDisplayName ||
  "Response action";

const getTemplateIdentifier = (template) =>
  extractTemplateUUID(template) ??
  template?.responseActionTemplateId ??
  template?.ResponseActionTemplateId ??
  template?.templateUUID ??
  template?.TemplateUUID ??
  template?.templateId ??
  template?.TemplateId ??
  template?.id ??
  template?.ID ??
  null;

const extractTemplateMetaFromAction = (action) => {
  const templateSources = [
    action?.template,
    action?.Template,
    action?.responseActionTemplate,
    action?.ResponseActionTemplate,
    action?.templateInfo,
    action?.templateData,
  ];

  for (const source of templateSources) {
    if (source) {
      return {
        id: extractTemplateUUID(source),
        label: getTemplateLabel(source),
        description: source?.description || source?.Description || "",
      };
    }
  }

  const fallbackId =
    action?.templateUUID ??
    action?.TemplateUUID ??
    action?.templateId ??
    action?.TemplateId ??
    action?.responseActionTemplateId ??
    action?.ResponseActionTemplateId ??
    action?.template_uuid ??
    action?.template_id ??
    null;

  return {
    id: fallbackId,
    label:
      action?.actionName ||
      action?.ActionName ||
      action?.action ||
      action?.Action ||
      null,
    description: action?.description || action?.Description || action?.notes || "",
  };
};

const normalizeAction = (action, index) => {
  const status = (
    action?.status ||
    action?.state ||
    action?.currentStatus ||
    "pending"
  )
    .toString()
    .toLowerCase();

  const templateMeta = extractTemplateMetaFromAction(action);
  const templateId =
    templateMeta.id !== undefined && templateMeta.id !== null
      ? templateMeta.id.toString()
      : null;

  const actionUUID =
    action?.uuid ||
    action?.UUID ||
    action?.responseActionUUID ||
    action?.ResponseActionUUID ||
    null;

  return {
    id:
      actionUUID ||
      action?.id ||
      action?.responseActionId ||
      action?.actionId ||
      action?.templateId ||
      `action-${index}`,
    uuid: actionUUID,
    label:
      action?.action ||
      action?.title ||
      action?.name ||
      action?.actionName ||
      action?.templateActionName ||
      action?.template?.actionName ||
      "Response action",
    status,
    timestamp:
      action?.recordedTime ||
      action?.completedAt ||
      action?.completedOn ||
      action?.startedAt ||
      action?.updatedAt ||
      action?.createdOn ||
      action?.createdAt ||
      null,
    templateId,
    templateLabel: templateMeta.label,
    templateDescription: templateMeta.description,
  };
};

const normalizeRecommendation = (recommendation, index) => {
  const templateId =
    recommendation?.templateUUID ??
    recommendation?.TemplateUUID ??
    recommendation?.recommendationTemplateUUID ??
    recommendation?.RecommendationTemplateUUID ??
    recommendation?.templateId ??
    recommendation?.TemplateId ??
    recommendation?.recommendationTemplateId ??
    recommendation?.RecommendationTemplateId ??
    recommendation?.template?.id ??
    recommendation?.Template?.id ??
    null;

  const templateLabel =
    recommendation?.template?.name ||
    recommendation?.template?.actionName ||
    recommendation?.template?.title ||
    recommendation?.template?.Recommendation ||
    null;

  const templateDescription =
    recommendation?.template?.description ||
    recommendation?.template?.details ||
    recommendation?.template?.text ||
    null;

  const title =
    recommendation?.title ||
    recommendation?.name ||
    recommendation?.recommendation ||
    recommendation?.Recommendation ||
    recommendation?.actionName ||
    templateLabel ||
    `Recommendation ${index + 1}`;

  const recommendationUUID =
    recommendation?.uuid ||
    recommendation?.UUID ||
    recommendation?.recommendationUUID ||
    recommendation?.RecommendationUUID ||
    recommendation?.recommendationId ||
    recommendation?.RecommendationId ||
    recommendation?.id ||
    null;

  return {
    id:
      recommendationUUID ||
      recommendation?.recommendationId ||
      recommendation?.id ||
      `recommendation-${index}`,
    uuid: recommendationUUID,
    title,
    description:
      recommendation?.description ||
      recommendation?.details ||
      recommendation?.text ||
      recommendation?.body ||
      recommendation?.notes ||
      templateDescription ||
      "",
    status: (
      recommendation?.status ||
      recommendation?.state ||
      recommendation?.currentStatus ||
      "open"
    )
      .toString()
      .toLowerCase(),
    timestamp:
      recommendation?.recordedTime ||
      recommendation?.createdAt ||
      recommendation?.createdOn ||
      recommendation?.updatedAt ||
      recommendation?.updatedOn ||
      null,
    templateId: templateId ? templateId.toString() : null,
    templateLabel,
    templateDescription,
  };
};

const formatTimestamp = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// ─── Analysis Box ─────────────────────────────────────────────────────────────

function AnalysisBox({ text, onChange, onAnalyze, analyzing, onSave, saving, saved, saveError, disabled }) {
  return (
    <div className="analysis-box">
      <div className="analysis-box__header">
        <div className="analysis-box__header-left">
          <Brain size={15} className="analysis-box__brain-icon" />
          <span className="analysis-box__title">Analysis</span>
        </div>
        <div className="analysis-box__header-right">
          <button
            type="button"
            className="btn btn--primary btn--sm analysis-box__ai-btn"
            onClick={onAnalyze}
            disabled={analyzing || saving || disabled}
            title="Run AI analysis on this incident"
          >
            {analyzing ? (
              <><Loader2 size={13} className="spinning" />Analyzing…</>
            ) : (
              <><Sparkles size={13} />AI Analysis</>
            )}
          </button>
          <button
            type="button"
            className={`btn btn--sm analysis-box__save-btn${saved ? ' analysis-box__save-btn--saved' : ' btn--outline'}`}
            onClick={onSave}
            disabled={saving || analyzing || disabled}
            title="Save analysis"
          >
            {saving ? (
              <><Loader2 size={13} className="spinning" />Saving…</>
            ) : saved ? (
              <><CheckCheck size={13} />Saved</>
            ) : (
              <><Save size={13} />Save</>
            )}
          </button>
        </div>
      </div>
      <div className="analysis-box__body">
        <textarea
          className="analysis-box__textarea"
          placeholder="Add analysis notes for this incident…"
          value={text}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          disabled={analyzing}
        />
        {saveError && (
          <p className="analysis-box__error">{saveError}</p>
        )}
      </div>
    </div>
  );
}

export default function Response({ incident }) {
  const incidentUUID = useMemo(() => {
    if (!incident) {
      return null;
    }

    const source = incident._raw ?? incident;
    return (
      source?.incidentName ??
      source?.incidentUUID ??
      source?.id ??
      source?.incidentId ??
      source?.incidentNumber ??
      incident?.id
    );
  }, [incident]);

  const [responseActions, setResponseActions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragNodeRef = useRef(null);
  const [reorderingActions, setReorderingActions] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const templateMap = useMemo(() => {
    return templates.reduce((acc, template) => {
      const identifier = getTemplateIdentifier(template);
      if (!identifier) {
        return acc;
      }

      const rawKey = identifier.toString();
      const normalizedKey = normalizeGuid(rawKey);

      acc[rawKey] = template;
      if (normalizedKey) {
        acc[normalizedKey] = template;
      }

      return acc;
    }, {});
  }, [templates]);

  const [recommendationTemplates, setRecommendationTemplates] = useState([]);
  const [loadingRecommendationTemplates, setLoadingRecommendationTemplates] =
    useState(false);
  const recommendationTemplateMap = useMemo(() => {
    return recommendationTemplates.reduce((acc, template) => {
      const identifier = getTemplateIdentifier(template);
      if (!identifier) {
        return acc;
      }
      const rawKey = identifier.toString();
      const normalizedKey = normalizeGuid(rawKey);
      acc[rawKey] = template;
      if (normalizedKey) {
        acc[normalizedKey] = template;
      }
      return acc;
    }, {});
  }, [recommendationTemplates]);

  // ── AI Analysis state ──────────────────────────────────────────────────────
  const initialAnalysis = useMemo(() => {
    const raw = incident?._raw ?? incident ?? {};
    return (
      raw?.analysis ?? raw?.Analysis ??
      raw?.analystNotes ?? raw?.AnalystNotes ??
      raw?.analysisNotes ?? ''
    );
  }, [incident]);

  const [analysisText, setAnalysisText] = useState(initialAnalysis);
  const [analyzing, setAnalyzing] = useState(false);
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  const [analysisSaved, setAnalysisSaved] = useState(false);
  const [analysisSaveError, setAnalysisSaveError] = useState(null);

  const handleSaveAnalysis = async () => {
    if (!incidentUUID) return;
    try {
      setSavingAnalysis(true);
      setAnalysisSaved(false);
      setAnalysisSaveError(null);
      await apiClient.put(
        API_CONFIG.ENDPOINTS.INCIDENT_UPDATE(incidentUUID),
        { analysis: analysisText }
      );
      setAnalysisSaved(true);
      setTimeout(() => setAnalysisSaved(false), 2500);
    } catch (error) {
      console.error('Failed to save analysis', error);
      setAnalysisSaveError(error.response?.data?.message || error.message);
    } finally {
      setSavingAnalysis(false);
    }
  };

  const handleAiAnalyze = async () => {
    if (!incidentUUID) return;
    try {
      setAnalyzing(true);
      setGlobalError(null);

      const { data } = await apiClient.post(
        API_CONFIG.ENDPOINTS.INCIDENT_ANALYZE(incidentUUID),
        { additionalContext: '' }
      );

      const analysis = data?.analysis ?? data ?? {};

      // Map rootCauseAnalysis → Analysis textarea
      const rootCause =
        analysis?.rootCauseAnalysis ??
        analysis?.RootCauseAnalysis ??
        analysis?.summary ??
        '';
      setAnalysisText(rootCause);

      // Map immediateActions → Response Actions
      const immediateActions = Array.isArray(analysis?.immediateActions)
        ? analysis.immediateActions
        : [];

      for (let i = 0; i < immediateActions.length; i++) {
        const raw = immediateActions[i];
        const actionName =
          typeof raw === 'string' ? raw : raw?.action ?? raw?.name ?? String(raw);
        if (!actionName) continue;

        let templateUUID = null;
        try {
          const tplRes = await apiClient.post(
            API_CONFIG.ENDPOINTS.RESPONSE_ACTION_TEMPLATES,
            {
              actionName,
              name: actionName,
              title: actionName,
              description: '',
              defaultOrder: (responseActions?.length || 0) + i + 1,
              category: 'AI Generated',
            }
          );
          templateUUID = extractTemplateUUID(tplRes.data);
        } catch (_) { /* use null templateUUID */ }

        await apiClient.post(
          API_CONFIG.ENDPOINTS.INCIDENT_RESPONSE_ACTIONS(incidentUUID),
          {
            incidentUUID,
            templateUUID,
            templateId: templateUUID,
            responseActionTemplateId: templateUUID,
            actionName,
            action: actionName,
            name: actionName,
            status: 'pending',
            recordedTime: new Date().toLocaleTimeString('en-US', { hour12: false }),
            displayOrder: (responseActions?.length || 0) + i + 1,
          }
        );
      }

      // Map remediationSteps → Recommendations
      const remediationSteps = Array.isArray(analysis?.remediationSteps)
        ? analysis.remediationSteps
        : [];

      for (let i = 0; i < remediationSteps.length; i++) {
        const raw = remediationSteps[i];
        const title =
          typeof raw === 'string' ? raw : raw?.title ?? raw?.name ?? String(raw);
        if (!title) continue;

        let templateUUID = null;
        try {
          const tplRes = await apiClient.post(
            API_CONFIG.ENDPOINTS.RECOMMENDATION_TEMPLATES,
            {
              title,
              name: title,
              recommendation: title,
              description: '',
              category: 'AI Generated',
            }
          );
          templateUUID = extractTemplateUUID(tplRes.data);
        } catch (_) { /* use null templateUUID */ }

        await apiClient.post(
          API_CONFIG.ENDPOINTS.INCIDENT_RECOMMENDATIONS(incidentUUID),
          {
            incidentUUID,
            templateUUID,
            templateId: templateUUID,
            recommendationTemplateId: templateUUID,
            title,
            recommendation: title,
            name: title,
            description: '',
            status: 'open',
            recordedTime: new Date().toLocaleTimeString('en-US', { hour12: false }),
            displayOrder: (incidentRecommendations?.length || 0) + i + 1,
          }
        );
      }

      await fetchActions();
    } catch (error) {
      console.error('AI analysis failed', error);
      setGlobalError(error.response?.data?.message || error.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const [showForm, setShowForm] = useState(false);
  const [formState, setFormState] = useState(() => ({ ...INITIAL_FORM_STATE }));
  const [formError, setFormError] = useState(null);
  const [savingAction, setSavingAction] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  const [incidentRecommendations, setIncidentRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [showRecommendationForm, setShowRecommendationForm] = useState(false);
  const [recommendationFormState, setRecommendationFormState] = useState(() => ({
    ...INITIAL_RECOMMENDATION_FORM_STATE,
  }));
  const [recommendationError, setRecommendationError] = useState(null);
  const [savingRecommendation, setSavingRecommendation] = useState(false);
  const [deletingRecommendationTemplate, setDeletingRecommendationTemplate] =
    useState(false);

  const recommendationList = useMemo(
    () => incidentRecommendations,
    [incidentRecommendations]
  );

  const fetchActions = useCallback(async () => {
    if (!incidentUUID) {
      setResponseActions([]);
      setIncidentRecommendations([]);
      return;
    }

    try {
      setLoadingActions(true);
      setLoadingTemplates(true);
      setLoadingRecommendations(true);
      setLoadingRecommendationTemplates(true);
      setGlobalError(null);
      setRecommendationError(null);

      const bundleEndpoint =
        API_CONFIG.ENDPOINTS.INCIDENT_RESPONSE_ACTIONS_BUNDLE(incidentUUID);
      const { data } = await apiClient.get(bundleEndpoint, {
        headers: { Accept: "application/json" },
      });

      const bundleTemplates = normalizeArray(
        pluckBundleSection(data, [
          "responseActionTemplates",
          "responseActionTemplateDtos",
          "responseActionTemplateItems",
          "templates",
          "templateItems",
          "actionTemplates",
        ])
      );
      if (bundleTemplates.length) {
        setTemplates(bundleTemplates);
      } else {
        setTemplates([]);
        try {
          const templatesResponse = await apiClient.get(
            API_CONFIG.ENDPOINTS.RESPONSE_ACTION_TEMPLATES,
            { headers: { Accept: "application/json" } }
          );
          setTemplates(normalizeArray(templatesResponse.data));
        } catch (templateError) {
          console.warn("Failed to load response action templates", templateError);
        }
      }

      const bundleRecommendationTemplates = normalizeArray(
        pluckBundleSection(data, [
          "recommendationTemplates",
          "recommendationTemplateDtos",
          "recommendationTemplateItems",
          "incidentRecommendationTemplates",
        ])
      );

      if (bundleRecommendationTemplates.length) {
        setRecommendationTemplates(bundleRecommendationTemplates);
      } else {
        setRecommendationTemplates([]);
        try {
          const recTemplateResponse = await apiClient.get(
            API_CONFIG.ENDPOINTS.RECOMMENDATION_TEMPLATES,
            { headers: { Accept: "application/json" } }
          );
          setRecommendationTemplates(
            normalizeArray(recTemplateResponse.data)
          );
        } catch (recTemplateError) {
          console.warn(
            "Failed to load recommendation templates",
            recTemplateError
          );
        }
      }

      const bundleActions = normalizeArray(
        pluckBundleSection(data, [
          "responseActions",
          "incidentResponseActions",
          "incidentResponseActionDtos",
          "responseActionItems",
          "actions",
          "items",
        ])
      );

      const bundleRecommendations = normalizeArray(
        pluckBundleSection(data, [
          "incidentRecommendations",
          "recommendations",
          "responseRecommendations",
          "recommendationItems",
        ])
      );

      if (bundleRecommendations.length) {
        setIncidentRecommendations(
          bundleRecommendations.map((rec, index) =>
            normalizeRecommendation(rec, index)
          )
        );
      } else {
        try {
          const recResponse = await apiClient.get(
            API_CONFIG.ENDPOINTS.INCIDENT_RECOMMENDATIONS(incidentUUID),
            { headers: { Accept: "application/json" } }
          );
          const normalizedRecommendations = normalizeArray(recResponse.data).map(
            (rec, index) => normalizeRecommendation(rec, index)
          );
          setIncidentRecommendations(normalizedRecommendations);
        } catch (recError) {
          console.warn("Failed to load incident recommendations", recError);
          setIncidentRecommendations([]);
        }
      }

      if (bundleActions.length) {
        const normalized = bundleActions.map((action, index) =>
          normalizeAction(action, index)
        );
        setResponseActions(normalized);
      } else {
        const endpoint =
          API_CONFIG.ENDPOINTS.INCIDENT_RESPONSE_ACTIONS(incidentUUID);
        const fallbackResponse = await apiClient.get(endpoint, {
          headers: { Accept: "application/json" },
        });

        const normalized = normalizeArray(fallbackResponse.data).map(
          (action, index) => normalizeAction(action, index)
        );
        setResponseActions(normalized);
      }
    } catch (error) {
      console.error("Failed to load response actions", error);
      setGlobalError(error.response?.data?.message || error.message);
      setResponseActions([]);
      setIncidentRecommendations([]);
      setRecommendationError(error.response?.data?.message || error.message);
    } finally {
      setLoadingActions(false);
      setLoadingTemplates(false);
      setLoadingRecommendations(false);
      setLoadingRecommendationTemplates(false);
    }
  }, [incidentUUID]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  const handleTemplateChange = (event) => {
    const selectedId = event.target.value;
    if (!selectedId) {
      setFormState((prev) => ({
        ...prev,
        templateId: "",
        actionName: "",
        notes: "",
      }));
      return;
    }

    const template = templates.find((tpl) => {
      const tplId = getTemplateIdentifier(tpl);
      return tplId?.toString() === selectedId;
    });

    const templateUUID = getTemplateIdentifier(template);
    setFormState((prev) => ({
      ...prev,
      templateId: templateUUID,
      actionName: getTemplateLabel(template) || prev.actionName,
      notes: template?.description || prev.notes,
      status:
        (template?.defaultStatus || template?.status || prev.status)
          ?.toString()
          .toLowerCase() || prev.status,
    }));
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormError(null);
    setFormState({ ...INITIAL_FORM_STATE });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!incidentUUID) return;

    if (!formState.actionName && !formState.templateId) {
      setFormError("Provide a name or select a template before saving.");
      return;
    }

    try {
      setSavingAction(true);
      setFormError(null);

      let templateUUID = formState.templateId;

      if (!templateUUID) {
        const templatePayload = {
          actionName: formState.actionName,
          name: formState.actionName,
          title: formState.actionName,
          description: formState.notes,
          defaultOrder: (templates?.length || 0) + 1,
          category: "Custom",
        };

        const templateResponse = await apiClient.post(
          API_CONFIG.ENDPOINTS.RESPONSE_ACTION_TEMPLATES,
          templatePayload
        );

        templateUUID = extractTemplateUUID(templateResponse.data);
      }

      const displayOrder = (responseActions?.length || 0) + 1;
      const recordedTime = new Date().toLocaleTimeString("en-US", {
        hour12: false,
      });

      const actionPayload = {
        incidentUUID,
        templateUUID,
        templateId: templateUUID,
        responseActionTemplateId: templateUUID,
        actionName: formState.actionName,
        action: formState.actionName,
        name: formState.actionName,
        status: formState.status,
        recordedTime,
        displayOrder,
        notes: formState.notes,
      };

      await apiClient.post(
        API_CONFIG.ENDPOINTS.INCIDENT_RESPONSE_ACTIONS(incidentUUID),
        actionPayload
      );

      await fetchActions();
      handleCancel();
    } catch (error) {
      console.error("Failed to add response action", error);
      setFormError(error.response?.data?.message || error.message);
    } finally {
      setSavingAction(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!formState.templateId) {
      setFormError("Select a template to delete.");
      return;
    }

    try {
      setDeletingTemplate(true);
      setFormError(null);

      await apiClient.delete(
        API_CONFIG.ENDPOINTS.RESPONSE_ACTION_TEMPLATE(formState.templateId)
      );

      await fetchActions();
      handleCancel();
    } catch (error) {
      console.error("Failed to delete response action template", error);
      setFormError(error.response?.data?.message || error.message);
    } finally {
      setDeletingTemplate(false);
    }
  };

  const handleRecommendationFormChange = (event) => {
    const { name, value } = event.target;
    setRecommendationFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleRecommendationTemplateChange = (event) => {
    const selectedId = event.target.value;
    if (!selectedId) {
      setRecommendationFormState((prev) => ({
        ...prev,
        templateId: "",
        title: prev.title,
        description: prev.description,
      }));
      return;
    }

    const template = recommendationTemplates.find((tpl) => {
      const tplId = getTemplateIdentifier(tpl);
      return tplId?.toString() === selectedId;
    });

    const templateUUID = template ? getTemplateIdentifier(template) : null;
    setRecommendationFormState((prev) => ({
      ...prev,
      templateId: templateUUID,
      title: getTemplateLabel(template) || prev.title,
      description: template?.description || prev.description,
      status:
        (template?.defaultStatus || template?.status || prev.status)
          ?.toString()
          .toLowerCase() || prev.status,
    }));
  };

  const handleRecommendationCancel = () => {
    setShowRecommendationForm(false);
    setRecommendationError(null);
    setRecommendationFormState({ ...INITIAL_RECOMMENDATION_FORM_STATE });
  };

  const handleRecommendationSubmit = async (event) => {
    event.preventDefault();
    if (!incidentUUID) return;

    if (!recommendationFormState.title) {
      setRecommendationError("Recommendation requires a title.");
      return;
    }

    try {
      setSavingRecommendation(true);
      setRecommendationError(null);

      let templateUUID = recommendationFormState.templateId;

      if (!templateUUID) {
        const templatePayload = {
          title: recommendationFormState.title,
          name: recommendationFormState.title,
          recommendation: recommendationFormState.title,
          description: recommendationFormState.description,
          details: recommendationFormState.description,
          defaultStatus: recommendationFormState.status,
          status: recommendationFormState.status,
          category: "General",
        };

        const templateResponse = await apiClient.post(
          API_CONFIG.ENDPOINTS.RECOMMENDATION_TEMPLATES,
          templatePayload
        );

        templateUUID = extractTemplateUUID(templateResponse.data);
      }

      const displayOrder = (incidentRecommendations?.length || 0) + 1;
      const recordedTime = new Date().toLocaleTimeString("en-US", {
        hour12: false,
      });

      const payload = {
        incidentUUID,
        templateUUID,
        templateId: templateUUID,
        recommendationTemplateId: templateUUID,
        title: recommendationFormState.title,
        recommendation: recommendationFormState.title,
        name: recommendationFormState.title,
        description: recommendationFormState.description,
        details: recommendationFormState.description,
        status: recommendationFormState.status,
        recordedTime,
        displayOrder,
      };

      await apiClient.post(
        API_CONFIG.ENDPOINTS.INCIDENT_RECOMMENDATIONS(incidentUUID),
        payload
      );

      await fetchActions();
      handleRecommendationCancel();
    } catch (error) {
      console.error("Failed to add recommendation", error);
      setRecommendationError(error.response?.data?.message || error.message);
    } finally {
      setSavingRecommendation(false);
    }
  };

  const handleDeleteRecommendationTemplate = async () => {
    if (!recommendationFormState.templateId) {
      setRecommendationError("Select a template to delete.");
      return;
    }

    try {
      setDeletingRecommendationTemplate(true);
      setRecommendationError(null);

      await apiClient.delete(
        API_CONFIG.ENDPOINTS.RECOMMENDATION_TEMPLATE(
          recommendationFormState.templateId
        )
      );

      await fetchActions();
      handleRecommendationCancel();
    } catch (error) {
      console.error("Failed to delete recommendation template", error);
      setRecommendationError(error.response?.data?.message || error.message);
    } finally {
      setDeletingRecommendationTemplate(false);
    }
  };

  const removeResponseAction = async (action, { unlinkOnly = false } = {}) => {
    if (!incidentUUID || !action) return;

    const responseActionId =
      action?.uuid ||
      action?.UUID ||
      action?.responseActionUUID ||
      action?.ResponseActionUUID ||
      action?.id ||
      action?.responseActionId ||
      null;
    const templateId =
      action?.templateId ||
      action?.templateUUID ||
      action?.responseActionTemplateId ||
      action?.responseActionTemplateUUID ||
      null;
    const targetTemplateId = templateId || responseActionId;

    if (unlinkOnly && !responseActionId) {
      setGlobalError("Unable to unlink response action: missing action id.");
      return;
    }

    if (!unlinkOnly && !targetTemplateId) {
      setGlobalError("Unable to delete response action template: missing id.");
      return;
    }

    try {
      if (unlinkOnly) {
        await apiClient.delete(
          API_CONFIG.ENDPOINTS.INCIDENT_RESPONSE_ACTION(
            incidentUUID,
            responseActionId
          ),
          {
            params: { unlinkOnly: true },
          }
        );
      } else {
        await apiClient.delete(
          API_CONFIG.ENDPOINTS.RESPONSE_ACTION_TEMPLATE(targetTemplateId)
        );
      }
      await fetchActions();
    } catch (error) {
      console.error("Failed to modify response action", error);
      setGlobalError(error.response?.data?.message || error.message);
    }
  };

  const removeRecommendation = async (
    recommendation,
    { unlinkOnly = false } = {}
  ) => {
    if (!incidentUUID || !recommendation) return;

    const recommendationId =
      recommendation?.uuid ||
      recommendation?.UUID ||
      recommendation?.recommendationUUID ||
      recommendation?.RecommendationUUID ||
      recommendation?.recommendationId ||
      recommendation?.RecommendationId ||
      recommendation?.id ||
      null;

    if (!recommendationId) {
      setRecommendationError("Unable to modify recommendation: missing id.");
      return;
    }

    try {
      await apiClient.delete(
        API_CONFIG.ENDPOINTS.INCIDENT_RECOMMENDATION(
          incidentUUID,
          recommendationId
        ),
        {
          params: unlinkOnly ? { unlinkOnly: true } : undefined,
        }
      );
      await fetchActions();
    } catch (error) {
      console.error("Failed to modify recommendation", error);
      setRecommendationError(error.response?.data?.message || error.message);
    }
  };

  // Drag and drop handlers for reordering response actions
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    dragNodeRef.current = e.target;

    setTimeout(() => {
      if (dragNodeRef.current) {
        dragNodeRef.current.classList.add("response-action--dragging");
      }
    }, 0);

    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.outerHTML);
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragLeave = (e) => {
    const relatedTarget = e.relatedTarget;
    if (!e.currentTarget.contains(relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newActions = [...responseActions];
    const draggedAction = newActions[draggedIndex];

    newActions.splice(draggedIndex, 1);
    newActions.splice(dropIndex, 0, draggedAction);

    // Update displayOrder for all actions
    const reorderedActions = newActions.map((action, index) => ({
      ...action,
      displayOrder: index + 1,
    }));

    // Optimistically update UI
    setResponseActions(reorderedActions);

    // Call API to persist reorder using bulk endpoint
    try {
      setReorderingActions(true);
      setGlobalError(null);

      // Prepare bulk reorder payload with all actions
      const reorderPayload = reorderedActions.map((action, index) => {
        const responseActionId =
          action?.uuid ||
          action?.UUID ||
          action?.responseActionUUID ||
          action?.ResponseActionUUID ||
          action?.id ||
          action?.responseActionId ||
          null;

        return {
          responseActionId: responseActionId,
          displayOrder: index + 1,
        };
      }).filter((item) => item.responseActionId); // Filter out any invalid actions

      // Validate payload
      if (reorderPayload.length === 0) {
        throw new Error("No valid actions to reorder");
      }

      // Validate no duplicate action IDs
      const actionIds = reorderPayload.map((a) => a.responseActionId);
      if (actionIds.length !== new Set(actionIds).size) {
        throw new Error("Duplicate action IDs found");
      }

      // Validate no duplicate display orders
      const displayOrders = reorderPayload.map((a) => a.displayOrder);
      if (displayOrders.length !== new Set(displayOrders).size) {
        throw new Error("Duplicate display orders found");
      }

      // Call bulk reorder endpoint
      await apiClient.put(
        API_CONFIG.ENDPOINTS.INCIDENT_RESPONSE_ACTIONS_REORDER(incidentUUID),
        { actions: reorderPayload },
        { headers: { Accept: "application/json" } }
      );
    } catch (error) {
      console.error("Failed to reorder response actions", error);
      setGlobalError(
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        "Failed to reorder response actions"
      );
      // Revert to original order on error
      await fetchActions();
    } finally {
      setReorderingActions(false);
      setDraggedIndex(null);
      setDragOverIndex(null);
    }
  };

  const handleDragEnd = () => {
    if (dragNodeRef.current) {
      dragNodeRef.current.classList.remove("response-action--dragging");
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
    dragNodeRef.current = null;
  };

  const renderActions = () => {
    if (!incidentUUID) {
      return (
        <div className="response__empty-state">
          <AlertCircle size={16} />
          <span>Select an incident to view the linked response actions.</span>
        </div>
      );
    }

    if (loadingActions) {
      return (
        <div className="response__empty-state">
          <Loader2 size={16} className="spinning" />
          <span>Loading response actions…</span>
        </div>
      );
    }

    if (responseActions.length === 0) {
      return (
        <div className="response__empty-state">
          <AlertCircle size={16} />
          <span>No response actions yet. Use “Add Response Action” to create one.</span>
        </div>
      );
    }

  return (
        <div className="response-actions">
          {responseActions.map((action, index) => {
          const statusMeta = STATUS_META[action.status] || STATUS_META.default;
          const IconComponent = statusMeta.icon;
          const timestamp = formatTimestamp(action.timestamp);
          const template =
            (action.templateId && templateMap[action.templateId]) ||
            (action.templateId &&
              templateMap[normalizeGuid(action.templateId)]) ||
            null;
          const actionTitle =
            action.templateLabel ||
            (template && getTemplateLabel(template)) ||
            action.label;
          const actionDescription =
            action.templateDescription ||
            template?.description ||
            template?.Description ||
            null;

            return (
            <div 
              key={action.id ?? index} 
              className={`response-action ${
                dragOverIndex === index ? "response-action--drag-over" : ""
              } ${draggedIndex === index ? "response-action--dragging" : ""}`}
              draggable={!reorderingActions}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
                <div 
                  className="response-action__drag-handle" 
                  title="Drag to reorder"
                  style={{ pointerEvents: reorderingActions ? 'none' : 'auto' }}
                >
                  <GripVertical size={16} />
                </div>
                <div className="response-action__indicator">
                  <div 
                    className="response-action__dot" 
                  style={{ backgroundColor: statusMeta.color }}
                  >
                    <IconComponent size={12} color="white" />
                  </div>
                </div>
                
                <div className="response-action__content">
                  <div className="response-action__header">
                  <div className="response-action__action">{actionTitle}</div>
                    <div className="response-action__badges">
                    <span className={statusMeta.className}>{action.status}</span>
                    {timestamp && (
                      <span className="response-action__timestamp">
                        {timestamp}
                      </span>
                    )}
                  </div>
                </div>
                {/* {actionDescription && (
                  <p className="response-action__description">
                    {actionDescription}
                  </p>
                )} */}
                <div className="response-action_description_and_Button">
                {actionDescription && (
                  <p className="response-action__description">
                    {actionDescription}
                  </p>
                )}
                  <div className="response-action__controls">
                 
                  <button
                    type="button"
                    className="btn btn--outline btn--sm btn--icon"
                    onClick={() =>
                      removeResponseAction(action, { unlinkOnly: true })
                    }
                    disabled={!incidentUUID}
                    title="Unlink"
                  >
                    <img
                     src={UnlinkIcon}
                     alt="Unlink"
                     color="white"
                    width={16}
                    height={16}
                  />

                  </button>
            
                   </div>
                   </div>
                </div>
              </div>
            );
          })}
      </div>
    );
  };

  const renderRecommendations = () => {
    if (loadingRecommendations && !recommendationList.length) {
      return (
        <div className="response__empty-state">
          <Loader2 size={16} className="spinning" />
          <span>Loading recommendations…</span>
        </div>
      );
    }

    if (!recommendationList.length) {
      return (
        <div className="response__empty-state">
          <AlertCircle size={16} />
          <span>No recommendations available for this incident.</span>
      </div>
      );
    }

    return (
        <div className="recommendations">
        {recommendationList.map((recommendation, index) => {
          const template =
            (recommendation.templateId &&
              recommendationTemplateMap[recommendation.templateId]) ||
            (recommendation.templateId &&
              recommendationTemplateMap[
                normalizeGuid(recommendation.templateId)
              ]) ||
            null;

          const title =
            recommendation.templateLabel ||
            (template && getTemplateLabel(template)) ||
            recommendation.title ||
            `Recommendation ${index + 1}`;

          const description =
            recommendation.description ||
            recommendation.templateDescription ||
            template?.description ||
            template?.Description ||
            "";

          return (
            <div key={recommendation.id ?? index} className="recommendation-card">
              <div className="recommendation-card__icon">
                <Zap size={16} color="#0B7AFF" />
              </div>
              <div className="recommendation-card__text">
                <div className="recommendation-card__title">{title}</div>
                {description && (
                  <p className="recommendation-card__description">{description}</p>
                )}
                <div className="recommendation-card__actions">
                  <button
                    type="button"
                    className="btn btn--outline btn--sm"
                    onClick={() =>
                      removeRecommendation(recommendation, { unlinkOnly: true })
                    }
                    disabled={!incidentUUID}
                  >
                    Unlink
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderForm = () => {
    if (!showForm || !incidentUUID) {
      return null;
    }

    return (
      <form className="response-action-form" onSubmit={handleSubmit} noValidate>
        <div className="response-action-form__row">
          <div className="response-action-form__field">
            <label htmlFor="templateId">Main template</label>
            <select
              id="templateId"
              name="templateId"
              value={formState.templateId}
              onChange={handleTemplateChange}
            >
              <option value="">
                {loadingTemplates ? "Loading templates…" : "Create new template"}
              </option>
              {templates.map((template) => {
                const tplId = getTemplateIdentifier(template);
                if (!tplId) {
                  return null;
                }
                const label = getTemplateLabel(template);

                return (
                  <option key={tplId} value={tplId.toString()}>
                    {label || "Untitled template"}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="response-action-form__field">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={formState.status}
              onChange={handleFormChange}
            >
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        <div className="response-action-form__field">
          <label htmlFor="actionName">Response action</label>
          <input
            id="actionName"
            name="actionName"
            type="text"
            placeholder="e.g. Block malicious IP addresses"
            value={formState.actionName}
            onChange={handleFormChange}
            required={!formState.templateId}
          />
        </div>

        <div className="response-action-form__field">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            name="notes"
            rows={3}
            placeholder="Additional context or remediation steps"
            value={formState.notes}
            onChange={handleFormChange}
          />
        </div>

        {formError && <div className="response__error">{formError}</div>}

        <div className="response-action-form__actions">
          <button
            type="button"
            className="btn btn--danger btn--sm"
            onClick={handleDeleteTemplate}
            disabled={!formState.templateId || deletingTemplate || savingAction}
          >
            {deletingTemplate ? (
              <>
                <Loader2 size={14} className="spinning" />
                Deleting…
              </>
            ) : (
              "Delete"
            )}
          </button>
          <button
            type="button"
            className="btn btn--outline btn--sm"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary btn--sm"
            disabled={savingAction}
          >
            {savingAction ? (
              <>
                <Loader2 size={14} className="spinning" />
                Saving…
              </>
            ) : (
              <>
                <Plus size={14} />
                Save & Link
              </>
            )}
          </button>
        </div>
      </form>
    );
  };

  return (
    <div className="response">

      {/* ── Analysis Box ── */}
      <AnalysisBox
        text={analysisText}
        onChange={setAnalysisText}
        onAnalyze={handleAiAnalyze}
        analyzing={analyzing}
        onSave={handleSaveAnalysis}
        saving={savingAnalysis}
        saved={analysisSaved}
        saveError={analysisSaveError}
        disabled={!incidentUUID}
      />

      <div className="response__section">
        <div className="response__section-header">
          <h3 className="response__section-title">Response Actions</h3>
          <div className="response__section-actions">
            <button
              className="btn btn--primary btn--sm"
              onClick={fetchActions}
              disabled={loadingActions || !incidentUUID}
            >
              <RefreshCw size={14} className={loadingActions ? "spinning" : ""} />
              Refresh
            </button>
            <button
              className="btn btn--primary btn--sm"
              onClick={() => setShowForm((prev) => !prev)}
              disabled={!incidentUUID}
            >
              <Plus size={14} />
              {showForm ? "Hide Form" : "Add Response Action"}
            </button>
          </div>
        </div>

        {globalError && <div className="response__error">{globalError}</div>}

        {renderForm()}
        {renderActions()}
      </div>

      <div className="response__section">
        <div className="response__section-header">
          <h3 className="response__section-title">Recommendations</h3>
          <div className="response__section-actions">
            <button
              className="btn btn--primary btn--sm"
              onClick={fetchActions}
              disabled={loadingRecommendations || !incidentUUID}
            >
              <RefreshCw
                size={14}
                className={loadingRecommendations ? "spinning" : ""}
              />
              Refresh
            </button>
            <button
              className="btn btn--primary btn--sm"
              onClick={() => setShowRecommendationForm((prev) => !prev)}
              disabled={!incidentUUID}
            >
              <Plus size={14} />
              {showRecommendationForm ? "Hide Form" : "Add Recommendation"}
            </button>
          </div>
        </div>

        {recommendationError && (
          <div className="response__error">{recommendationError}</div>
        )}

        {showRecommendationForm && incidentUUID && (
          <form
            className="response-action-form"
            onSubmit={handleRecommendationSubmit}
            noValidate
          >
            <div className="response-action-form__field">
              <label htmlFor="recommendationTemplate">Template</label>
              <select
                id="recommendationTemplate"
                name="recommendationTemplate"
                value={recommendationFormState.templateId}
                onChange={handleRecommendationTemplateChange}
              >
                <option value="">
                  {loadingRecommendationTemplates
                    ? "Loading templates…"
                    : "Create new template"}
                </option>
                {recommendationTemplates.map((template) => {
                  const tplId = getTemplateIdentifier(template);
                  if (!tplId) {
                    return null;
                  }
                  const label = getTemplateLabel(template);
                  return (
                    <option key={tplId} value={tplId.toString()}>
                      {label || "Untitled template"}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="response-action-form__field">
              <label htmlFor="recommendationTitle">Recommendation</label>
              <input
                id="recommendationTitle"
                name="title"
                type="text"
                placeholder="e.g. Coordinate endpoint re-imaging"
                value={recommendationFormState.title}
                onChange={handleRecommendationFormChange}
                required
              />
            </div>

            <div className="response-action-form__field">
              <label htmlFor="recommendationDescription">Details</label>
              <textarea
                id="recommendationDescription"
                name="description"
                rows={3}
                placeholder="Provide additional remediation guidance"
                value={recommendationFormState.description}
                onChange={handleRecommendationFormChange}
              />
            </div>

            <div className="response-action-form__actions">
              <button
                type="button"
                className="btn btn--danger btn--sm"
                onClick={handleDeleteRecommendationTemplate}
                disabled={
                  !recommendationFormState.templateId ||
                  deletingRecommendationTemplate ||
                  savingRecommendation
                }
              >
                {deletingRecommendationTemplate ? (
                  <>
                    <Loader2 size={14} className="spinning" />
                    Deleting…
                  </>
                ) : (
                  "Delete"
                )}
              </button>
              <button
                type="button"
                className="btn btn--outline btn--sm"
                onClick={handleRecommendationCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn--primary btn--sm"
                disabled={savingRecommendation}
              >
                {savingRecommendation ? (
                  <>
                    <Loader2 size={14} className="spinning" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Plus size={14} />
                    Save Recommendation
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {renderRecommendations()}
      </div>
    </div>
  );
}
